import { parsePhoneNumber } from 'libphonenumber-js';

import {
  ALLOWED_REGION_COUNTRY_CODES,
  IS_AUSTRALIA,
  PHONE_EXAMPLE_PRETTY,
  PHONE_FORMAT_EXAMPLES,
  REGION_COUNTRY_CODE,
} from '../../config/region';

export interface PhoneValidationResult {
  isValid: boolean;
  normalized?: string;
  error?: string;
}

/**
 * Phone validation with required country code format.
 *
 * The AU bundle accepts AU and IN (LT-427); the IN bundle accepts IN only.
 * Numbers from any other country are rejected so users always see numbers
 * in formats this deployment can actually dial.
 */
export function validatePhoneNumber(
  phone: string
): PhoneValidationResult {
  try {
    const cleanPhone = phone.trim().replace(/\s/g, '');

    if (!cleanPhone) {
      return {
        isValid: false,
        error: 'Phone number is required',
      };
    }

    // AU-only guard: users commonly drop the country code and type the mobile
    // short form "04..." — warn them with an AU-specific hint.
    if (IS_AUSTRALIA && /^04/.test(cleanPhone)) {
      return {
        isValid: false,
        error:
          `Numbers starting with 04 are not supported. Use +61 with country code (e.g. ${PHONE_EXAMPLE_PRETTY}).`,
      };
    }

    const isAllowedCountry = (
      country: string | undefined
    ): country is 'AU' | 'IN' =>
      !!country && (ALLOWED_REGION_COUNTRY_CODES as readonly string[]).includes(country);

    if (cleanPhone.startsWith('+')) {
      try {
        const pn = parsePhoneNumber(cleanPhone);
        if (pn && isAllowedCountry(pn.country) && pn.isValid()) {
          return { isValid: true, normalized: pn.format('E.164') };
        }
      } catch {}
    } else {
      try {
        const pn = parsePhoneNumber(cleanPhone, REGION_COUNTRY_CODE);
        if (pn && isAllowedCountry(pn.country) && pn.isValid()) {
          return { isValid: true, normalized: pn.format('E.164') };
        }
      } catch {}
    }

    return {
      isValid: false,
      error:
        `Invalid phone number. Use country code format (e.g. ${PHONE_EXAMPLE_PRETTY}).`,
    };
  } catch {
    return {
      isValid: false,
      error:
        `Invalid phone number. Supported format includes country code (e.g. ${PHONE_EXAMPLE_PRETTY}).`,
    };
  }
}

export function validateEmail(email: string): boolean {
  if (!email) return true;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function validateRequiredField(value: string, _fieldName: string): boolean {
  return value.trim().length > 0;
}

export function getPhoneFormatExamples(): string[] {
  return [...PHONE_FORMAT_EXAMPLES];
}
